import {useState, useMemo, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import styled from 'styled-components';
import {Body1, Body2} from '@ui/atoms/typography';
import {CancelIcon} from '@ui/atoms/icons';
import {BackAction} from '@ui/molecules/back-action';
import {Button} from '@ui/atoms/button';
import {useStoreContext} from '@store/store-context';
import {CommentDetails} from '@ui/layouts/users-goals/comments';
import {useFirebaseFetch} from '@hooks/query-hook';
import {Avatar} from '@ui/atoms/avatar';
import {TextArea} from '@ui/molecules/field/textArea/textArea';
import dayjs from 'dayjs';
import {parseName} from '../my-checkins-list';
import {SubmissionRecord} from './submission-card';
import {setFirebaseData, getFirebaseData} from '@utils/firebase-handler';
import {AddMentions} from '@utils/firebase-request';
import {TabNavigation} from '@ui/molecules/tab-nav';
import {Drawer} from '@ui/molecules/drawer';
import {getWeekRange} from '@utils/date';

const formatSubmissionDate = (submission: SubmissionRecord) => {
  if (submission.weekOfYear && submission.year) {
    const range = getWeekRange(submission.weekOfYear, submission.year);
    return dayjs(range.starts).format('MMM D, YYYY');
  }

  if (submission.submittedAt) {
    return dayjs(submission.submittedAt).format('MMM D, YYYY');
  }

  if (submission.dueDay) {
    return dayjs(submission.dueDay).format('MMM D, YYYY');
  }

  return dayjs().format('MMM D, YYYY');
};

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const SubmissionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubmissionHeader = styled.div`
  background: #f6f6f8;
  border-radius: 8px;
  padding: 4px 8px;
  display: inline-block;
  width: fit-content;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border: 1px solid #ededf2;
  padding: 24px;
  border-radius: 10px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

interface CheckinSubmissionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  submission: SubmissionRecord;
  activeTab?: 'comments' | 'notes';
}

export const CheckinSubmissionDrawer = observer(
  ({
    isOpen,
    onClose,
    submission,
    activeTab = 'comments',
  }: CheckinSubmissionDrawerProps) => {
    const {
      authStore: {auth},
    } = useStoreContext();

    const [currentTab, setCurrentTab] = useState<'comments' | 'notes'>(
      activeTab,
    );
    const [notesText, setNotesText] = useState('');
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    const [notesExistInDB, setNotesExistInDB] = useState(false);

    // Get user ID - handle both string and object formats
    const userId =
      typeof submission.user === 'string'
        ? submission.user
        : submission.user && typeof submission.user === 'object'
        ? (submission.user as {id?: string}).id
        : undefined;

    // Firebase path for comments: checkins/comments/{submissionId}/{userId}
    const commentsPath = `checkins/comments/${submission.id}/${userId}`;

    // Firebase path for private notes: checkins/notes/{submissionId}/{managerId}
    const notesPath = `checkins/notes/${submission.id}/${auth.user.id}`;

    const {data: commentsData = {}} = useFirebaseFetch(
      `${commentsPath}/comments`,
    );

    const userName = parseName(submission.user);
    const userJobTitle =
      typeof submission.user === 'object' && submission.user !== null
        ? (submission.user as {jobTitle?: string})?.jobTitle || ''
        : '';

    const submissionDate = formatSubmissionDate(submission);

    // Subscribers for comments (manager and submission owner)
    const defaultSubscribers = useMemo(
      () => [auth.user.id, userId].filter(Boolean) as string[],
      [auth.user.id, userId],
    );

    const navigations = useMemo(
      () => [
        {
          active: currentTab === 'comments',
          href: '#',
          id: 'comments',
          onClick: (e: any) => {
            e.preventDefault();
            setCurrentTab('comments');
          },
          title: 'Comments',
        },
        {
          active: currentTab === 'notes',
          href: '#',
          id: 'notes',
          onClick: (e: any) => {
            e.preventDefault();
            setCurrentTab('notes');
          },
          title: 'Private note',
        },
      ],
      [currentTab],
    );

    // Mark mentions as read when comments tab is opened
    useEffect(() => {
      if (currentTab === 'comments' && submission.id) {
        // Mark unread comments as read
        AddMentions(
          auth.user.id,
          submission.id,
          {
            unreadComment: 0,
            mentionedBy: [],
          },
          `checkins/comments`,
        );
      }
    }, [currentTab, auth.user.id, submission.id]);

    // Load notes from Firebase when tab changes to notes
    useEffect(() => {
      if (currentTab === 'notes') {
        const loadNotes = async () => {
          try {
            const data = await getFirebaseData(`${notesPath}/content`);
            if (data) {
              setNotesText(data.text || '');
              setIsEditingNotes(false);
              setNotesExistInDB(true);
            } else {
              // No notes exist, enable editing mode
              setNotesText('');
              setIsEditingNotes(true);
              setNotesExistInDB(false);
            }
          } catch (error) {
            console.error('Error loading notes:', error);
            setNotesText('');
            setIsEditingNotes(true);
            setNotesExistInDB(false);
          }
        };
        loadNotes();
      }
    }, [currentTab, notesPath]);

    const handleSaveNotes = async () => {
      setIsSavingNotes(true);
      try {
        await setFirebaseData(`${notesPath}/content`, {
          text: notesText,
          updatedAt: Date.now(),
          updatedBy: auth.user.id,
        });
        setIsEditingNotes(false);
        setNotesExistInDB(true);
      } catch (error) {
        console.error('Error saving notes:', error);
      } finally {
        setIsSavingNotes(false);
      }
    };

    const handleCancelNotes = async () => {
      // Reload original notes
      try {
        const data = await getFirebaseData(`${notesPath}/content`);
        if (data) {
          setNotesText(data.text || '');
        } else {
          setNotesText('');
        }
        setIsEditingNotes(false);
      } catch (error) {
        console.error('Error reloading notes:', error);
        setNotesText('');
      }
    };

    const handleEditNotes = () => {
      setIsEditingNotes(true);
    };

    const hasNotes = notesText.trim().length > 0;

    if (!isOpen) {
      return null;
    }

    return (
      <Drawer variant="narrow">
        <DrawerContent>
          <div className="flex justify-end w-full">
            <BackAction
              icon={<CancelIcon />}
              title="Close"
              onClick={onClose}
              width="xs"
            />
          </div>

          <HeaderSection>
            <SubmissionInfo>
              <SubmissionHeader>
                <Body2 kind="textBody" weight="semibold">
                  Submission for {submissionDate}
                </Body2>
              </SubmissionHeader>
              <UserInfo>
                <Avatar
                  tooltip={true}
                  userId={userId || ''}
                  name={userName}
                  size="md"
                  timezone={
                    typeof submission.user === 'object' &&
                    submission.user !== null
                      ? submission.user.timezone
                      : undefined
                  }
                  src={
                    typeof submission.user === 'object' &&
                    submission.user !== null
                      ? (submission.user as {avatar?: {url?: string}})?.avatar
                          ?.url
                      : undefined
                  }
                />
                <div>
                  <Body2 weight="semibold" kind="textDark">
                    {userName}
                  </Body2>
                  <Body2 kind="textBody" style={{fontSize: '14px'}}>
                    {userJobTitle}
                  </Body2>
                </div>
              </UserInfo>
            </SubmissionInfo>
          </HeaderSection>

          <TabNavigation navigations={navigations} />

          <TabContent>
            {currentTab === 'comments' && (
              <CommentDetails
                pathId={submission.id}
                path={`${commentsPath}`}
                mentionsPath={`checkins/comments/${submission.id}`}
                allGoalSubscribers={defaultSubscribers}
                defaultSubscribers={defaultSubscribers}
                allowedMentionedUsers={defaultSubscribers}
                comments={{comments: commentsData}}
              />
            )}

            {currentTab === 'notes' && (
              <NotesContainer>
                <TextArea
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  placeholder="Write your private note about this submission..."
                  disabled={!isEditingNotes}
                  inputStyle={{minHeight: '150px'}}
                  margin
                />

                <ButtonGroup>
                  {isEditingNotes ? (
                    <>
                      {notesExistInDB && (
                        <Button
                          kind="secondary"
                          onClick={handleCancelNotes}
                          style={{padding: '5px 10px'}}
                          disabled={isSavingNotes}>
                          Cancel
                        </Button>
                      )}
                      <Button
                        kind="primary"
                        onClick={handleSaveNotes}
                        isLoading={isSavingNotes}
                        style={{padding: '5px 10px'}}
                        disabled={isSavingNotes}>
                        Save note
                      </Button>
                    </>
                  ) : (
                    <Button
                      kind="secondary"
                      onClick={handleEditNotes}
                      style={{padding: '5px 10px'}}
                      disabled={!hasNotes && notesText.trim().length === 0}>
                      Edit
                    </Button>
                  )}
                </ButtonGroup>
              </NotesContainer>
            )}
          </TabContent>
        </DrawerContent>
      </Drawer>
    );
  },
);
